<!-- 試用版申込フォーム STEP2：Customer ID・オプション選択・規約同意を行う画面 -->
<template>
  <v-container fluid class="pa-0">
    <PageHeader />

    <div class="content-container">
      <!-- STEPナビ -->
      <StepNavigator :current="2" />

      <v-form ref="step2FormRef" @submit.prevent>
        <!-- ===============================
         ご依頼内容
    =============================== -->
        <section class="form-section">
          <h2>ご依頼内容</h2>

          <p class="info-descA">
            ご希望する Customer ID のご入力ください。<br />
            ※既存の Customer ID
            と重複した際、ご希望に添えない場合がございます。予めご了承ください。
          </p>

          <!-- Customer ID -->
          <v-row class="form-row" no-gutters>
            <v-col cols="12" md="3" class="form-label">
              顧客 ID（Customer ID）
              <span class="req">必須</span>
            </v-col>

            <v-col cols="12" md="9" class="pl-md-12">
              <v-text-field
                v-model="form.customerId"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :rules="[rules.requiredCustomerId]"
                @blur="onCustomerIdBlur"
              />
              <p class="note-small mt-1">
                ※Customer
                IDは、アルファベット小文字1文字以上12文字以下でご入力ください。
              </p>
            </v-col>
          </v-row>

          <!-- ご選択オプション -->
          <v-row class="form-row" no-gutters>
            <v-col cols="12" md="3" class="form-label">
              ご選択オプション（複数選択可）
            </v-col>

            <v-col cols="12" md="9" class="pl-md-12">
              <v-checkbox
                v-model="form.options.windows"
                label="Windowsログオン認証オプション"
                hide-details
                density="compact"
              />
              <v-checkbox
                v-model="form.options.idManager"
                label="IDマネージャーオプション"
                hide-details
                density="compact"
              />
              <v-checkbox
                v-model="form.options.fingerprint"
                label="指紋認証オプション"
                hide-details
                density="compact"
              />
              <v-checkbox
                v-model="form.options.iccard"
                label="ICカードオプション*"
                hide-details
                density="compact"
              />
            </v-col>
          </v-row>

          <p class="note">
            ※ICカードおよびカードリーダー（RC-S300/S）はお客様にてご用意ください。
          </p>
        </section>

        <!-- ▼ 指紋認証ユニット -->
        <section v-if="form.options.fingerprint" class="fingerprint-section">
          <h2>指紋認証ユニットの貸出</h2>

          <p class="info-desc">
            上記オプション選択にて「指紋認証オプション」をご選択の場合、指紋認証ユニット（UBF-Touch）の貸出を行っております。<br />
            ご希望の場合は、貸出台数（上限3台）、指紋認証ユニットの送付先をお選びください。
          </p>

          <v-row class="form-row" no-gutters>
            <v-col cols="12" md="3" class="form-label"> 貸出台数 </v-col>

            <v-col cols="12" md="9" class="pl-md-12">
              <div class="d-flex align-center">
                <v-select
                  v-model="form.rental.quantity"
                  :items="[1, 2, 3]"
                  variant="outlined"
                  density="compact"
                  hide-details
                  style="max-width: 100px"
                />
                <span class="ml-2">台</span>
              </div>
            </v-col>
          </v-row>

          <v-row class="form-row" no-gutters align="center">
            <v-col cols="12" md="3" class="form-label">
              貸出送付先
              <span class="req">必須</span>
            </v-col>

            <v-col
              cols="12"
              md="9"
              class="form-input pl-md-10 d-flex align-center"
            >
              <v-radio-group
                v-model="form.rental.receiver"
                inline
                hide-details="auto"
                class="mt-0"
                :rules="[rules.required]"
              >
                <v-radio label="ご利用になるお客様" value="customer" />
                <v-radio label="管理者様" value="manager" />
                <v-radio label="販売代理店" value="agency" />
              </v-radio-group>
            </v-col>
          </v-row>
        </section>

        <!-- ===============================
         規約への承諾
    =============================== -->
        <section class="form-section">
          <h2>利用規約への承諾 並びに 個人情報保護方針への同意</h2>

          <p class="info-desc">
            お申込みの際には「
            <a
              href="https://www.dds.co.jp/wp-content/uploads/2025/06/evecloud-termsofservice_D230351.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              利用規約
            </a>
            」をご確認のうえ、ご承諾いただきますようお願い申し上げます。<br />
            また、弊社におけるお申込み情報の取扱いについて「
            <a
              href="https://www.dds.co.jp/ja/privacypolicy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              個人情報保護方針
            </a>
            」をご確認いただき、ご同意いただきますようお願いいたします。
          </p>

          <!-- 利用規約 -->
          <v-row class="form-row" no-gutters>
            <v-col cols="12" md="3" class="form-label">
              利用規約の確認
              <span class="req">必須</span>
            </v-col>

            <v-col cols="12" md="9" class="pl-md-12">
              <v-checkbox
                v-model="form.agreeTerms"
                label="承諾する"
                hide-details="auto"
                density="compact"
                :rules="[rules.requiredCheck]"
              />
            </v-col>
          </v-row>

          <!-- 個人情報保護方針 -->
          <v-row class="form-row" no-gutters>
            <v-col cols="12" md="3" class="form-label">
              個人情報保護方針の確認
              <span class="req">必須</span>
            </v-col>

            <v-col cols="12" md="9" class="pl-md-12">
              <v-checkbox
                v-model="form.agreePrivacy"
                label="同意する"
                hide-details="auto"
                density="compact"
                :rules="[rules.requiredCheck]"
              />
            </v-col>
          </v-row>
        </section>
      </v-form>

      <!-- ボタン -->
      <div class="button-area two-buttons">
        <v-btn
          class="button-secondary"
          @click="goBack"
          :disabled="isCheckingCustomerId"
          size="large"
          variant="outlined"
        >
          戻る
        </v-btn>
        <v-btn
          class="button-primary"
          @click="goNext"
          :disabled="isCheckingCustomerId"
          :loading="isCheckingCustomerId"
          size="large"
        >
          確認
        </v-btn>
      </div>
    </div>
  </v-container>
</template>

<script setup lang="ts">
import { useRouter } from "vue-router";
import { computed, ref, nextTick, watch } from "vue";
import { useExperienceFormStore } from "../store/useExperienceFormStore.ts";
import { markStepCompleted } from "../router/index.ts";
import StepNavigator from "../components/StepNavigator.vue";
import PageHeader from "../components/layout/PageHeader.vue";

const step2FormRef = ref<any>(null);
const customerIdError = ref("");
const isCheckingCustomerId = ref(false);
const customerIdChecked = ref(false);

const router = useRouter();
const store = useExperienceFormStore();
const form = computed(() => store.form);

// Customer IDが変更されたらチェック状態をリセット
watch(
  () => form.value.customerId,
  () => {
    customerIdChecked.value = false;
    customerIdError.value = "";
  },
);

/* =========================
   Vuetify Rules
========================= */
const rules = {
  required: (v: any) => !!v || "必須項目です。",
  requiredCheck: (v: boolean) => v === true || "必須項目です。",
  requiredCustomerId: (v: string) => {
    if (!v) return "必須項目です。";
    if (!/^[a-z0-9]{1,12}$/.test(v)) {
      return "Customer IDは小文字英字・数字1〜12文字で入力してください。";
    }
    if (customerIdError.value) {
      return customerIdError.value;
    }
    return true;
  },
};

const onCustomerIdBlur = async () => {
  customerIdError.value = "";
  customerIdChecked.value = false;

  if (!form.value.customerId) return;

  isCheckingCustomerId.value = true;
  try {
    const ok = await store.checkCustomerId(form.value.customerId);
    if (!ok) {
      customerIdError.value = store.error;
      // フォームを再検証してエラーを表示
      step2FormRef.value?.validate();
    } else {
      customerIdChecked.value = true;
    }
  } finally {
    isCheckingCustomerId.value = false;
  }
};

function goBack() {
  router.push("/step1");
}

async function goNext() {
  // Vuetifyのバリデーションを実行
  const { valid } = await step2FormRef.value.validate();

  if (!valid) {
    // バリデーションエラーがある場合はスクロールして表示
    await nextTick();
    const firstError = document.querySelector(".v-input--error");
    if (firstError) {
      firstError.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
    return;
  }

  // Customer IDの重複チェックが未完了の場合は実行
  if (form.value.customerId && !customerIdChecked.value) {
    isCheckingCustomerId.value = true;
    try {
      const ok = await store.checkCustomerId(form.value.customerId);
      if (!ok) {
        customerIdError.value = store.error;
        // エラーを表示するため再検証
        await step2FormRef.value.validate();

        // エラー箇所にスクロール
        await nextTick();
        const errorField = document.querySelector(".v-input--error");
        if (errorField) {
          errorField.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
        return;
      }
      customerIdChecked.value = true;
    } finally {
      isCheckingCustomerId.value = false;
    }
  }

  // 最終確認: Customer IDエラーがある場合は進まない
  if (customerIdError.value) {
    return;
  }

  // 指紋認証オプションが選択されている場合、送付先チェック
  if (form.value.options.fingerprint && !form.value.rental.receiver) {
    alert("指紋認証ユニットの送付先を選択してください。");
    return;
  }

  // 貸出送付先に選ばれた区分の情報が STEP1 で入力されているか確認する。
  // 貸出品の発送先なので、情報が無いと運用側が宛先を特定できない。
  if (form.value.options.fingerprint) {
    if (form.value.rental.receiver === "manager" && !store.form.managerEnabled) {
      alert(
        "「貸出送付先」で「管理者様」が選択されています。STEP1に戻って管理者情報を入力してください。",
      );
      return;
    }

    if (form.value.rental.receiver === "agency" && !store.form.agencyEnabled) {
      alert(
        "「貸出送付先」で「販売代理店」が選択されています。STEP1に戻って販売代理店情報を入力してください。",
      );
      return;
    }
  }

  // ステップ2完了をマーク
  markStepCompleted("step2");
  router.push("/step3");
}
</script>

<style scoped lang="scss">
@use "@/assets/styles/experience-form.scss" as *;
</style>
